import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { SvgCross, SvgDrag } from '../assets/images'
import { FocusTrap } from 'focus-trap-react'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import { createPortal } from 'react-dom'
import { v4 as uuid } from 'uuid'

interface ModalSize {
  width: number
  height: number
}

interface ModalPosition {
  x: number
  y: number
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

interface ModalProps {
  children: ReactNode
  isOpen: boolean
  closeModal: () => void
  modalHeader?: string
  headerClasses?: string
  className?: string
  showCloseButton?: boolean
  width?: string
  closeModalOnOutsideClick?: boolean
  cssClassName?: string
  bodyCssClassName?: string
  allowOutsideClick?: boolean
  isDragable?: boolean
  isResizable?: boolean
  isBackdropInteractive?: boolean
  preventBackgroundInteraction?: boolean
  disableFocusTrap?: boolean
  minResizeWidth?: number
  minResizeHeight?: number
}

const MIN_CONTENT_ROW_HEIGHT = 200
const MIN_CONTENT_WIDTH = 280

const RESIZE_HANDLES: {
  dir: ResizeDirection
  className: string
}[] = [
  { dir: 'n', className: 'top-0 left-2 right-2 h-1.5 cursor-ns-resize' },
  { dir: 's', className: 'bottom-0 left-2 right-2 h-1.5 cursor-ns-resize' },
  { dir: 'e', className: 'top-2 bottom-2 right-0 w-1.5 cursor-ew-resize' },
  { dir: 'w', className: 'top-2 bottom-2 left-0 w-1.5 cursor-ew-resize' },
  { dir: 'ne', className: 'top-0 right-0 h-3 w-3 cursor-nesw-resize' },
  { dir: 'nw', className: 'top-0 left-0 h-3 w-3 cursor-nwse-resize' },
  { dir: 'se', className: 'bottom-0 right-0 h-3 w-3 cursor-nwse-resize' },
  { dir: 'sw', className: 'bottom-0 left-0 h-3 w-3 cursor-nesw-resize' },
]

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const Modal = ({
  children,
  isOpen,
  closeModal,
  modalHeader,
  headerClasses,
  showCloseButton = true,
  width = 'w-fit max-w-[98%]',
  closeModalOnOutsideClick = false,
  cssClassName,
  bodyCssClassName,
  allowOutsideClick = true,
  isDragable = true,
  isResizable = false,
  isBackdropInteractive = false,
  preventBackgroundInteraction = false,
  disableFocusTrap = false,
  minResizeWidth = MIN_CONTENT_WIDTH,
  minResizeHeight = MIN_CONTENT_ROW_HEIGHT,
}: ModalProps) => {
  const modalContainer = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const handleClassRef = useRef(`drag-handle-${uuid()}`)
  const handleClass = handleClassRef.current
  const minSizeRef = useRef<ModalSize | null>(null)
  const sizeRef = useRef<ModalSize | null>(null)
  const positionRef = useRef<ModalPosition>({ x: 0, y: 0 })
  const [size, setSize] = useState<ModalSize | null>(null)
  const [position, setPosition] = useState<ModalPosition>({ x: 0, y: 0 })

  sizeRef.current = size
  positionRef.current = position

  const [viewportTick, setViewportTick] = useState(0)

  useEffect(() => {
    if (!isOpen) {
      setSize(null)
      setPosition({ x: 0, y: 0 })
      minSizeRef.current = null
      setViewportTick(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    let raf = 0
    const onWindowResize = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          setViewportTick((tick) => tick + 1)
        })
      })
    }

    window.addEventListener('resize', onWindowResize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onWindowResize)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen || viewportTick === 0) return

    const modal = modalContainer.current
    if (!modal) return

    const vw = window.innerWidth
    const vh = window.innerHeight
    const minW = minSizeRef.current?.width ?? minResizeWidth
    const minH = minSizeRef.current?.height ?? minResizeHeight

    const currentSize = sizeRef.current
    if (currentSize) {
      const nextW = clamp(currentSize.width, minW, vw)
      const nextH = clamp(currentSize.height, minH, Math.max(minH, vh))
      if (nextW !== currentSize.width || nextH !== currentSize.height) {
        modal.style.width = `${nextW}px`
        modal.style.height = `${nextH}px`
        const nextSize = { width: nextW, height: nextH }
        sizeRef.current = nextSize
        setSize(nextSize)
      }
    }

    const rect = modal.getBoundingClientRect()
    let dx = 0
    let dy = 0

    if (rect.left < 0) dx += -rect.left
    if (rect.left + dx + rect.width > vw) {
      dx = vw - rect.width - rect.left
    }
    if (rect.left + dx < 0) dx = -rect.left

    if (rect.top < 0) dy += -rect.top
    if (rect.top + dy + rect.height > vh) {
      dy = vh - rect.height - rect.top
    }
    if (rect.top + dy < 0) dy = -rect.top

    let nextPos = positionRef.current
    if (dx !== 0 || dy !== 0) {
      nextPos = {
        x: positionRef.current.x + dx,
        y: positionRef.current.y + dy,
      }
      modal.style.transform = `translate(${nextPos.x}px, ${nextPos.y}px)`
      positionRef.current = nextPos
      setPosition(nextPos)
    }

    const verified = modal.getBoundingClientRect()
    const stillOffScreen =
      verified.left < -1 ||
      verified.top < -1 ||
      verified.right > vw + 1 ||
      verified.bottom > vh + 1

    if (stillOffScreen) {
      modal.style.transform = 'translate(0px, 0px)'
      positionRef.current = { x: 0, y: 0 }
      setPosition({ x: 0, y: 0 })
    }
  }, [isOpen, viewportTick])

  useLayoutEffect(() => {
    if (!isOpen || !isResizable) return

    const modal = modalContainer.current
    const scroll = scrollContainerRef.current
    if (!modal || !scroll) return

    const chromeHeight = modal.offsetHeight - scroll.clientHeight
    const chromeWidth = modal.offsetWidth - scroll.clientWidth
    minSizeRef.current = {
      width: chromeWidth + minResizeWidth,
      height: chromeHeight + minResizeHeight,
    }
  }, [isOpen, isResizable, modalHeader])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const modal = modalContainer?.current
      const dropdownRoot = document.getElementById('dropdown-root')
      if (
        closeModalOnOutsideClick &&
        modal &&
        !modal.contains(event.target as Node) &&
        !dropdownRoot?.contains(event.target as Node)
      ) {
        closeModal()
      }
    }
    if (closeModalOnOutsideClick) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeModal, closeModalOnOutsideClick])

  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const modal = modalContainer.current
    if (!isOpen || !modal) return

    previouslyFocusedElementRef.current = document.activeElement as HTMLElement

    const focusableElements = Array.from(
      modal.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null)

    const firstEl = focusableElements[0]
    if (firstEl) firstEl.focus()
    else modal.focus()
    return () => {
      previouslyFocusedElementRef.current?.focus?.()
    }
  }, [isOpen])

  const handleDrag = useCallback(
    (_event: DraggableEvent, data: DraggableData) => {
      setPosition({ x: data.x, y: data.y })
    },
    []
  )

  const handleResizeStart = useCallback(
    (direction: ResizeDirection) =>
      (event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()

        const modal = modalContainer.current
        if (!modal) return

        const rect = modal.getBoundingClientRect()
        const startWidth = rect.width
        const startHeight = rect.height
        const startX = event.clientX
        const startY = event.clientY
        const startPos = { ...position }
        const minWidth = minSizeRef.current?.width ?? minResizeWidth
        const minHeight = minSizeRef.current?.height ?? minResizeHeight
        const maxWidth = window.innerWidth
        const maxHeight = window.innerHeight
        const growsWest = direction.includes('w')
        const growsNorth = direction.includes('n')
        const growsEast = direction.includes('e')
        const growsSouth = direction.includes('s')

        const heightCeiling = growsNorth
          ? rect.bottom
          : growsSouth
          ? window.innerHeight - rect.top
          : maxHeight
        const widthCeiling = growsWest
          ? rect.right
          : growsEast
          ? window.innerWidth - rect.left
          : maxWidth

        const boundedMaxHeight = Math.max(
          minHeight,
          Math.min(maxHeight, heightCeiling)
        )
        const boundedMaxWidth = Math.max(
          minWidth,
          Math.min(maxWidth, widthCeiling)
        )

        const handlePointerMove = (moveEvent: PointerEvent) => {
          const deltaX = moveEvent.clientX - startX
          const deltaY = moveEvent.clientY - startY
          let nextWidth = startWidth
          let nextHeight = startHeight

          if (growsEast) nextWidth = startWidth + deltaX
          if (growsWest) nextWidth = startWidth - deltaX
          if (growsSouth) nextHeight = startHeight + deltaY
          if (growsNorth) nextHeight = startHeight - deltaY

          nextWidth = clamp(nextWidth, minWidth, boundedMaxWidth)
          nextHeight = clamp(nextHeight, minHeight, boundedMaxHeight)

          const xSign = growsEast ? 1 : growsWest ? -1 : 0
          const ySign = growsSouth ? 1 : growsNorth ? -1 : 0

          setSize({ width: nextWidth, height: nextHeight })
          setPosition({
            x: startPos.x + (xSign * (nextWidth - startWidth)) / 2,
            y: startPos.y + (ySign * (nextHeight - startHeight)) / 2,
          })
        }

        const handlePointerUp = () => {
          document.removeEventListener('pointermove', handlePointerMove)
          document.removeEventListener('pointerup', handlePointerUp)
        }

        document.addEventListener('pointermove', handlePointerMove)
        document.addEventListener('pointerup', handlePointerUp)
      },
    [position, minResizeWidth, minResizeHeight]
  )

  if (!isOpen) return null

  const hasExplicitSize = size !== null
  const panelStyle: CSSProperties = hasExplicitSize
    ? {
        maxHeight: '100vh',
        maxWidth: '100vw',
        width: size.width,
        height: size.height,
      }
    : { maxHeight: 'calc(100vh - 2rem)' }

  const modalContent = (
    <FocusTrap
      active={!disableFocusTrap}
      focusTrapOptions={{
        initialFocus: false,
        allowOutsideClick,
        fallbackFocus: () => modalContainer.current || document.body,
      }}
    >
      <div
        className={`fixed inset-0 flex justify-center items-center z-[40] ${
          isBackdropInteractive
            ? 'bg-transparent pointer-events-none'
            : 'bg-black bg-opacity-50'
        } ${cssClassName ?? ''}`}
        onClick={(e) => preventBackgroundInteraction && e.stopPropagation()}
      >
        <button
          style={{
            opacity: 0,
            position: 'absolute',
            pointerEvents: 'none',
          }}
          tabIndex={0}
          aria-hidden="true"
        >
          Fallback
        </button>

        <Draggable
          nodeRef={modalContainer}
          handle={`.${handleClass}`}
          cancel=".modal-body-container input, textarea, select, .modal-resize-handle"
          bounds="body"
          disabled={!isDragable}
          position={position}
          onDrag={handleDrag}
        >
          <div
            ref={modalContainer}
            className={`bg-accent-0 rounded-[1rem] shadow-lg ${
              isBackdropInteractive
                ? 'pointer-events-auto px-5 pb-5 pt-6 border border-accent-350'
                : 'p-3'
            } ${hasExplicitSize ? '' : width} ${
              bodyCssClassName ?? ''
            } relative${
              hasExplicitSize
                ? ' flex flex-col overflow-hidden'
                : modalHeader
                ? ''
                : ' flex'
            }`}
            style={panelStyle}
          >
            {isDragable && (
              <div className="absolute top-3 left-4 right-4 translate-y-[-50%] z-10">
                <button
                  type="button"
                  className={`${handleClass} cursor-move w-full flex justify-center`}
                >
                  <SvgDrag className="rotate-90" />
                </button>
              </div>
            )}

            {modalHeader ? (
              <div className="border-b border-accent-100 pb-2 shrink-0">
                <div
                  className={`flex justify-between items-center bg-accent-50 rounded shadow pl-5 pr-1 py-4 ${
                    headerClasses ?? ''
                  }`}
                >
                  <div className="font-primary font-medium text-base">
                    {modalHeader}
                  </div>
                  {showCloseButton && (
                    <div
                      className="cursor-pointer p-1"
                      onClick={closeModal}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter' || e?.key === ' ') {
                          closeModal()
                          e?.preventDefault()
                        }
                      }}
                    >
                      <span className="text-black">
                        <SvgCross />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              showCloseButton && (
                <div
                  className="absolute top-2 right-2 cursor-pointer p-1 z-10"
                  onClick={closeModal}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      closeModal()
                      e?.preventDefault()
                    }
                  }}
                >
                  <span className="text-black">
                    <SvgCross />
                  </span>
                </div>
              )
            )}
            <div
              ref={scrollContainerRef}
              className={`modal-scroll-container flex-1 ${
                hasExplicitSize
                  ? 'min-h-0 min-w-0 overflow-auto'
                  : 'overflow-y-auto hide-scrollbar'
              }`}
              style={{ overscrollBehavior: 'contain' }}
            >
              <div className="modal-body-container w-full h-full">
                {children}
              </div>
            </div>

            {isResizable &&
              RESIZE_HANDLES.map(({ dir, className }) => (
                <div
                  key={dir}
                  className={`modal-resize-handle absolute z-20 ${className} ${
                    dir.length === 2 ? 'z-30' : ''
                  }`}
                  onPointerDown={handleResizeStart(dir)}
                  aria-hidden="true"
                />
              ))}
          </div>
        </Draggable>
      </div>
    </FocusTrap>
  )

  return createPortal(modalContent, document.body)
}

export default Modal
